import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import api from '../api';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', department: '' });
  const [alias, setAlias] = useState('');
  const [rolling, setRolling] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/auth/departments').then(res => setDepartments(res.data.departments));
  }, []);

  const generateAlias = async (dept) => {
    if (!dept) return;
    setRolling(true);
    try {
      const { data } = await api.post('/auth/generate-alias', { department: dept });
      setAlias(data.alias);
    } catch { setError('Failed to generate alias'); }
    setRolling(false);
  };

  const handleDepartmentChange = (e) => {
    const dept = e.target.value;
    setForm(f => ({ ...f, department: dept }));
    generateAlias(dept);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!alias) { setError('Please select a department to generate your alias'); return; }
    setSubmitting(true);
    try {
      await register({ ...form, alias });
      navigate('/');
    } catch (err) { setError(err.response?.data?.error || 'Registration failed'); }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: `1px solid ${tokens.edge}`,
    padding: '13px 16px',
    color: tokens.textHi,
    fontSize: 15,
    fontFamily: tokens.fontBody,
    outline: 'none',
    boxSizing: 'border-box',
    transition: `border-color 300ms ${tokens.ease}`,
  };

  const labelStyle = {
    display: 'block',
    fontFamily: tokens.fontBody, fontSize: 11,
    color: tokens.textLow, letterSpacing: '0.14em',
    textTransform: 'uppercase', marginBottom: 8,
  };

  const focusIn = e => { e.currentTarget.style.borderColor = tokens.accent; };
  const focusOut = e => { e.currentTarget.style.borderColor = tokens.edge; };

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.ground,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px',
      position: 'relative',
    }}>
      <div className="gl-atmosphere" />
      <div className="gl-grain" />

      <div style={{
        width: '100%', maxWidth: 380,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        position: 'relative', zIndex: 2,
        animation: `gl-fade-up 600ms ${tokens.ease}`,
      }}>

        {/* Wordmark */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 9,
          fontFamily: tokens.fontMono, fontSize: 11,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: tokens.accent, fontWeight: 500,
          marginBottom: 56,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: tokens.accent,
            boxShadow: `0 0 10px ${tokens.accent}`,
            animation: 'gl-breathe 3.5s ease-in-out infinite',
          }} />
          ghostlight
        </div>

        {/* Heading */}
        <h1 style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 32, fontWeight: tokens.displayWeight,
          letterSpacing: tokens.displayTracking,
          color: tokens.textHi,
          margin: 0, textAlign: 'center',
        }}>
          Step inside.
        </h1>

        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 15, color: tokens.textMid,
          margin: '12px 0 0', textAlign: 'center',
        }}>
          Your real name stays with us. Everyone else sees your alias.
        </p>

        {/* Error */}
        {error && (
          <div style={{
            width: '100%', marginTop: 28,
            padding: '12px 16px',
            color: tokens.danger, fontSize: 13, fontFamily: tokens.fontBody,
            border: `1px solid ${tokens.danger}33`,
            background: `${tokens.danger}0a`,
          }}>{error}</div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 40 }}>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Your name</label>
            <input type="text" required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Never displayed publicly"
              style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            <p style={{
              color: tokens.textDim, fontSize: 11, fontFamily: tokens.fontBody,
              marginTop: 6, marginBottom: 0,
            }}>Private. Admin-only, for safety.</p>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Email</label>
            <input type="email" required value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Password</label>
            <input type="password" required minLength={8} value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="At least 8 characters"
              style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Department</label>
            <select required value={form.department}
              onChange={handleDepartmentChange}
              style={{ ...inputStyle, background: tokens.ground, appearance: 'auto' }}
              onFocus={focusIn} onBlur={focusOut}>
              <option value="">Select your field...</option>
              {departments.map(d => <option key={d} value={d} style={{ background: tokens.house }}>{d}</option>)}
            </select>
          </div>

          {/* Alias display */}
          {alias && (
            <div style={{
              background: tokens.accentGlow,
              border: `1px solid ${tokens.accent}33`,
              padding: '24px',
              textAlign: 'center',
              marginBottom: 28,
            }}>
              <div style={{
                fontFamily: tokens.fontBody, fontSize: 10,
                color: tokens.textLow, letterSpacing: '0.14em',
                textTransform: 'uppercase', marginBottom: 10,
              }}>Your alias on GhostLight</div>
              <div style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 28, color: tokens.accent,
                letterSpacing: tokens.displayTracking,
              }}>{alias}</div>
              <button type="button" onClick={() => generateAlias(form.department)}
                disabled={rolling}
                className="gl-btn-bare"
                style={{
                  marginTop: 12,
                  color: tokens.accentDim, fontSize: 12,
                  fontFamily: tokens.fontBody, letterSpacing: '0.04em',
                  opacity: rolling ? 0.4 : 1,
                }}>
                {rolling ? 'Rolling...' : 'Re-roll alias'}
              </button>
              <p style={{
                color: tokens.textDim, fontSize: 11, fontFamily: tokens.fontBody,
                marginTop: 10, marginBottom: 0,
              }}>This is how the community will know you.</p>
            </div>
          )}

          <button type="submit" disabled={submitting || !alias}
            style={{
              width: '100%',
              border: `1px solid ${tokens.accent}`,
              background: tokens.accentGlow,
              color: tokens.accent,
              fontFamily: tokens.fontBody, fontWeight: 500,
              fontSize: 14, letterSpacing: '0.04em',
              padding: '15px',
              cursor: (submitting || !alias) ? 'default' : 'pointer',
              transition: `background 300ms ${tokens.ease}`,
              opacity: (submitting || !alias) ? 0.4 : 1,
            }}
            onMouseEnter={e => { if (!submitting && alias) e.currentTarget.style.background = `${tokens.accent}22`; }}
            onMouseLeave={e => { e.currentTarget.style.background = tokens.accentGlow; }}
          >
            {submitting ? 'Creating account...' : 'Join GhostLight'}
          </button>
        </form>

        {/* Divider */}
        <div style={{ width: '100%', height: 1, background: tokens.edge, margin: '36px 0' }} />

        {/* Bottom links */}
        <div style={{ textAlign: 'center' }}>
          <p style={{
            fontFamily: tokens.fontBody, fontSize: 13,
            color: tokens.textMid, margin: 0,
          }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: tokens.accent, textDecoration: 'none' }}>Sign in</Link>
          </p>

          <p style={{
            fontFamily: tokens.fontBody, fontSize: 11,
            color: tokens.textDim, margin: '24px 0 0',
          }}>
            By joining, you agree to our{' '}
            <Link to="/terms" style={{ color: tokens.textLow, textDecoration: 'underline', textUnderlineOffset: 3 }}>Terms</Link>
            {' & '}
            <Link to="/privacy" style={{ color: tokens.textLow, textDecoration: 'underline', textUnderlineOffset: 3 }}>Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
